// MathRenderer renders text that may contain LaTeX math delimiters.
// Inline math:  $...$       e.g.  "Solve $2x + 3 = 7$"
// Display math: $$...$$     e.g.  "$$\frac{x+1}{2}$$"
// Plain text (no delimiters) is rendered unchanged — fully backward compatible
// with existing non-math questions.

import React from 'react';
import katex from 'katex';

const renderLatex = (latex, displayMode) => {
  try {
    return katex.renderToString(latex, {
      displayMode,
      throwOnError: false,
      strict: false,
    });
  } catch {
    return latex;
  }
};

// Splits content on $$...$$ and $...$ delimiters and returns an array of
// React elements — math parts rendered via KaTeX, plain text left as-is.
const MathRenderer = ({ content, className = '', style = {} }) => {
  if (!content) return null;

  const parts = [];
  // Match $$...$$ (display) before $...$ (inline) to avoid double-matching.
  // Use negative lookbehind to skip escaped \$ (literal dollar signs).
  //
  // Inline content pattern: (?:[^\\$\n]|\\.)+?
  //   Allows any non-special char OR any backslash-escape (e.g. \$, \%, \times).
  //   This lets currency like \$18{,}000 appear inside $...$  math without breaking.
  const pattern = /(?<!\\)(\$\$[\s\S]*?(?<!\\)\$\$|(?<!\\)\$(?:[^\\$\n]|\\.)+?(?<!\\)\$)/g;
  let lastIndex = 0;
  let key = 0;
  let match;

  // Helper: replace \$ with literal $ in plain text segments
  const unescapeDollar = (text) => text.replace(/\\\$/g, '$');

  while ((match = pattern.exec(content)) !== null) {
    // Plain text segment before this match
    if (match.index > lastIndex) {
      parts.push(
        <span key={key++} style={{ whiteSpace: 'pre-line' }}>
          {unescapeDollar(content.slice(lastIndex, match.index))}
        </span>
      );
    }

    const full = match[0];
    const isDisplay = full.startsWith('$$');
    const latex = isDisplay ? full.slice(2, -2).trim() : full.slice(1, -1).trim();
    const html = renderLatex(latex, isDisplay);

    parts.push(
      <span
        key={key++}
        dangerouslySetInnerHTML={{ __html: html }}
        style={isDisplay ? { display: 'block', textAlign: 'left', margin: '8px 0' } : undefined}
      />
    );

    lastIndex = match.index + full.length;
  }

  // Any remaining plain text after the last match
  if (lastIndex < content.length) {
    parts.push(
      <span key={key++} style={{ whiteSpace: 'pre-line' }}>
        {unescapeDollar(content.slice(lastIndex))}
      </span>
    );
  }

  // No delimiters found — render as plain text (backward compatible)
  if (parts.length === 0) {
    return (
      <span className={className} style={{ whiteSpace: 'pre-line', ...style }}>
        {content}
      </span>
    );
  }

  return (
    <span className={className} style={style}>
      {parts}
    </span>
  );
};

export default MathRenderer;
